import { makeVar } from '@apollo/client';

import {
  ICartItem,
  ICartMetadata,
  IOrderSummary,
} from '@/imports/ui/components/Cart/TypeDefs';

// Cart state with dynamic order summary calculated on backend
export interface CartState {
  items: ICartItem[];
  orderSummary: IOrderSummary | null;
  cartMetadata: ICartMetadata | null;
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  // Subscription state
  subscriptionConnected: boolean;
  // Promo code state
  appliedPromoCode: string | null;
  promoCodeLoading: boolean;
  availablePromoCodes: any[] | null; // Available promo codes
}

const initialCartState: CartState = {
  items: [],
  orderSummary: null,
  cartMetadata: null,
  loading: false,
  error: null,
  lastUpdated: null,
  subscriptionConnected: false,
  appliedPromoCode: null,
  promoCodeLoading: false,
  availablePromoCodes: null,
};

// Reactive variable for cart state
export const cartVar = makeVar<CartState>(initialCartState);

// Helper functions to update cart state
export const cartActions = {
  setLoading: (loading: boolean) => {
    const current = cartVar();
    cartVar({
      ...current,
      loading,
      error: loading ? null : current.error,
    });
  },

  // Apply minimal delta update from change stream
  // Returns true if full cart refetch is needed (pricing/order summary changed)
  applyDeltaUpdate: (
    itemId: string,
    eventType: string,
    delta: { updatedFields: any; removedFields: string[] } | null
  ): boolean => {
    const current = cartVar();
    const item = current.items.find((item) => item._id === itemId);
    
    if (!item || !delta) {
      // Item not found or no delta - need full refetch
      return true;
    }

    const updatedFields = delta.updatedFields || {};

    // Events that require full refetch (affect pricing/order summary)
    const pricingEvents = [
      'CART_ITEM_QUANTITY_UPDATED',
      'CART_ITEM_METADATA_UPDATED',
    ];

    if (pricingEvents.includes(eventType)) {
      return true; // Need full refetch for pricing changes
    }

    // For ATTENDEES_DETAILS_UPDATED, apply optimistic update
    if (eventType === 'ATTENDEES_DETAILS_UPDATED') {
      if (updatedFields.checkoutAttendeesDetails) {
        const updatedItems = current.items.map((existingItem) => {
          if (existingItem._id === itemId) {
            return {
              ...existingItem,
              checkoutAttendeesDetails: updatedFields.checkoutAttendeesDetails,
            };
          }
          return existingItem;
        });
        cartVar({
          ...current,
          items: updatedItems,
          lastUpdated: new Date(),
        });
        return false; // No refetch needed for attendee details
      }
    }

    // Unknown event or delta - refetch to be safe
    return true;
  },


  setError: (error: string | null) => {
    const current = cartVar();
    cartVar({
      ...current,
      error,
      loading: false,
    });
  },

  setPromoCodeLoading: (loading: boolean) => {
    const current = cartVar();
    cartVar({
      ...current,
      promoCodeLoading: loading,
    });
  },

  setSubscriptionConnected: (connected: boolean) => {
    const current = cartVar();
    cartVar({
      ...current,
      subscriptionConnected: connected,
    });
  },

  // Helper function to deep compare order summaries
  orderSummaryEquals: (
    a: IOrderSummary | null,
    b: IOrderSummary | null
  ): boolean => {
    if (a === b) return true;
    if (!a || !b) return false;

    // Compare all numeric fields
    if (
      a.discount !== b.discount ||
      a.subTotal !== b.subTotal ||
      a.total !== b.total ||
      a.membershipTotal !== b.membershipTotal ||
      a.eventTicketTotal !== b.eventTicketTotal ||
      a.discountCode !== b.discountCode ||
      (a as any).creditAvailable !== (b as any).creditAvailable ||
      (a as any).creditApplied !== (b as any).creditApplied ||
      (a as any).unusedAmount !== (b as any).unusedAmount ||
      (a as any).unusedAmountApplied !== (b as any).unusedAmountApplied ||
      (a as any).creditIssued !== (b as any).creditIssued ||
      (a as any).effectiveTotal !== (b as any).effectiveTotal
    ) {
      return false;
    }

    // Compare membership details
    if (
      a.membershipDetails?.membershipName !==
        b.membershipDetails?.membershipName ||
      a.membershipDetails?.unitPrice !== b.membershipDetails?.unitPrice
    ) {
      return false;
    }

    // Compare ticket details
    if (a.ticketDetails?.length !== b.ticketDetails?.length) {
      return false;
    }

    if (a.ticketDetails && b.ticketDetails) {
      for (let i = 0; i < a.ticketDetails.length; i++) {
        const ticketA = a.ticketDetails[i];
        const ticketB = b.ticketDetails[i];
        if (
          ticketA.quantity !== ticketB.quantity ||
          ticketA.ticketName !== ticketB.ticketName ||
          ticketA.unitPrice !== ticketB.unitPrice
        ) {
          return false;
        }
      }
    }

    return true;
  },

  // Update cart from item change data (only changed items + orderSummary)
  // This prevents flickering by only updating what actually changed
  updateCartFromItemChange: (itemChange: {
    changedItems: ICartItem[];
    removedItemId?: string | null;
    orderSummary: IOrderSummary;
    cartMetadata?: ICartMetadata;
    isCartCleared?: boolean; // Special flag for CART_CLEARED events
  }) => {
    const current = cartVar();

    // Start with current items - preserve references for unchanged items
    let newItems = current.items;
    let itemsArrayChanged = false;
    
    // Special handling for cart cleared - clear all items immediately
    if (itemChange.isCartCleared || (itemChange.changedItems.length === 0 && itemChange.orderSummary.total === 0)) {
      newItems = [];
      itemsArrayChanged = current.items.length > 0;
    } else {
        // Handle item removal first
        if (itemChange.removedItemId) {
          const itemExists = current.items.some(
            (item) => item._id === itemChange.removedItemId
          );
          if (itemExists) {
            newItems = current.items.filter(
              (item) => item._id !== itemChange.removedItemId
            );
            itemsArrayChanged = true;
          }
        }
        
        // Update or add changed items - preserve references for unchanged items
        if (itemChange.changedItems.length > 0) {
      const changedItemIds = new Set(
        itemChange.changedItems.map((item) => item._id)
      );
      
      // Create new array only if we need to update items
      // CRITICAL: Only update items that are in changedItems - all others keep their exact references
      // IMPORTANT: Use `newItems` (not `current.items`) so that any removal applied above is preserved.
      newItems = newItems.map((existingItem) => {
        // If this item was changed, use the new data from backend
        if (changedItemIds.has(existingItem._id)) {
          const changedItem = itemChange.changedItems.find(
            (item) => item._id === existingItem._id
          );
          if (changedItem) {
            itemsArrayChanged = true;
            // Create new object with all fields from backend
            return { ...changedItem };
          }
        }
        // CRITICAL: Preserve the existing reference completely - don't touch unchanged items
        // This ensures discountAmount and all other fields stay stable
        return existingItem;
      });
      
      // Add any new items that don't exist yet
      itemChange.changedItems.forEach((changedItem) => {
        const exists = current.items.some((item) => item._id === changedItem._id);
        if (!exists) {
          newItems = [...newItems, { ...changedItem }];
          itemsArrayChanged = true;
        }
      });
      }
    }

    // Always update orderSummary from item change.
    // Preserve existing credit fields if the incoming data doesn't include them
    // (e.g. change-stream delta events that don't go through getMyCart).
    const incoming = itemChange.orderSummary as any;
    const existing = current.orderSummary as any;
    const newOrderSummary = {
      ...incoming,
      creditAvailable: incoming.creditAvailable ?? existing?.creditAvailable ?? 0,
      creditApplied: incoming.creditApplied ?? existing?.creditApplied ?? 0,
      unusedAmount: incoming.unusedAmount ?? existing?.unusedAmount ?? 0,
      unusedAmountApplied: incoming.unusedAmountApplied ?? existing?.unusedAmountApplied ?? 0,
      creditIssued: incoming.creditIssued ?? existing?.creditIssued ?? 0,
      effectiveTotal: incoming.effectiveTotal ?? existing?.effectiveTotal ?? incoming.total,
    };

    // Update cartMetadata if provided
    let newCartMetadata = current.cartMetadata;
    if (itemChange.cartMetadata !== undefined) {
      const currentMeta = current.cartMetadata;
      const newMeta = itemChange.cartMetadata;
      if (
        !currentMeta ||
        currentMeta._id !== newMeta._id ||
        currentMeta.currentlyAppliedPromocode !==
          newMeta.currentlyAppliedPromocode ||
        currentMeta.createdAt !== newMeta.createdAt ||
        currentMeta.updatedAt !== newMeta.updatedAt
      ) {
        newCartMetadata = { ...itemChange.cartMetadata };
      }
    }

    // Check if anything actually changed
    // Use reference comparison for items (itemsArrayChanged tracks if we created a new array)
    const itemsChanged = itemsArrayChanged || newItems !== current.items;
    const orderSummaryChanged = !cartActions.orderSummaryEquals(
      current.orderSummary,
      newOrderSummary
    );
    const cartMetadataChanged = newCartMetadata !== current.cartMetadata;

    // Only update state if something actually changed
    if (itemsChanged || orderSummaryChanged || cartMetadataChanged) {
      const newState = {
        ...current,
        items: newItems,
        orderSummary: newOrderSummary,
        cartMetadata: newCartMetadata,
        appliedPromoCode:
          itemChange.cartMetadata?.currentlyAppliedPromocode !== undefined
            ? itemChange.cartMetadata.currentlyAppliedPromocode
            : current.appliedPromoCode,
        lastUpdated: new Date(),
        loading: false,
        error: null,
      };

      cartVar(newState);
    } else {
      // Even if nothing changed, ensure loading is false
      if (current.loading) {
        cartVar({
          ...current,
          loading: false,
        });
      }
    }
  },

  // Main method to update cart from GraphQL responses with dynamic order summary
  updateCartFromResponse: (response: {
    items?: ICartItem[];
    orderSummary?: IOrderSummary;
    cartMetadata?: ICartMetadata;
  }) => {
    const current = cartVar();

    // Preserve object references for unchanged items to prevent unnecessary re-renders
    let newItems = current.items;
    if (response.items !== undefined) {
      // Create a map of current items by ID for quick lookup
      const currentItemsMap = new Map(
        current.items.map((item) => [item._id, item])
      );

      newItems = response.items.map((newItem) => {
        const existingItem = currentItemsMap.get(newItem._id);

        // If item exists and hasn't changed, reuse the same object reference
        // Compare all relevant fields including discountAmount to prevent unnecessary re-renders
        if (
          existingItem &&
          existingItem.quantity === newItem.quantity &&
          existingItem.unitPrice === newItem.unitPrice &&
          existingItem.discountedPrice === newItem.discountedPrice &&
          existingItem.discountAmount === newItem.discountAmount &&
          existingItem.total === newItem.total &&
          existingItem.subTotal === newItem.subTotal &&
          existingItem.saleLabel === newItem.saleLabel &&
          existingItem.isOnSale === newItem.isOnSale
        ) {
          return existingItem; // Reuse existing reference
        }

        // Item changed or is new, create new object
        // CRITICAL: Preserve discountAmount from existing item if pricing hasn't changed
        // This prevents flickering when subscription sends discountAmount: 0 for unchanged items
        const itemPricingUnchanged =
          existingItem &&
          existingItem.quantity === newItem.quantity &&
          existingItem.unitPrice === newItem.unitPrice &&
          existingItem.discountedPrice === newItem.discountedPrice &&
          existingItem.subTotal === newItem.subTotal &&
          existingItem.total === newItem.total;

        // If pricing hasn't changed, preserve existing discountAmount to prevent flicker
        // Otherwise use the new discountAmount from backend
        const discountAmount = itemPricingUnchanged
          ? existingItem.discountAmount
          : newItem.discountAmount ?? existingItem?.discountAmount ?? 0;

        return {
          ...newItem,
          otherItemId: newItem.otherItemId ?? existingItem?.otherItemId,
          discountAmount, // Use preserved value if pricing unchanged, otherwise use new value
        };
      });
    }

    // Only update orderSummary if values actually changed
    let newOrderSummary = current.orderSummary;
    if (response.orderSummary !== undefined) {
      if (
        !cartActions.orderSummaryEquals(
          current.orderSummary,
          response.orderSummary
        )
      ) {
        // Preserve credit fields if the incoming response doesn't include them
        const incoming = response.orderSummary as any;
        const existing = current.orderSummary as any;
        newOrderSummary = {
          ...incoming,
          creditAvailable: incoming.creditAvailable ?? existing?.creditAvailable ?? 0,
          creditApplied: incoming.creditApplied ?? existing?.creditApplied ?? 0,
          unusedAmount: incoming.unusedAmount ?? existing?.unusedAmount ?? 0,
          unusedAmountApplied: incoming.unusedAmountApplied ?? existing?.unusedAmountApplied ?? 0,
          creditIssued: incoming.creditIssued ?? existing?.creditIssued ?? 0,
          effectiveTotal: incoming.effectiveTotal ?? existing?.effectiveTotal ?? incoming.total,
        };
      }
      // Otherwise, reuse existing reference to prevent re-renders
    }

    // Only update cartMetadata if values actually changed
    let newCartMetadata = current.cartMetadata;
    if (response.cartMetadata !== undefined) {
      const currentMeta = current.cartMetadata;
      const newMeta = response.cartMetadata;
      if (
        !currentMeta ||
        currentMeta._id !== newMeta._id ||
        currentMeta.currentlyAppliedPromocode !==
          newMeta.currentlyAppliedPromocode ||
        currentMeta.createdAt !== newMeta.createdAt ||
        currentMeta.updatedAt !== newMeta.updatedAt
      ) {
        newCartMetadata = { ...response.cartMetadata }; // Create new object reference only if changed
      }
      // Otherwise, reuse existing reference to prevent re-renders
    }

    // Only create a new state object if something actually changed
    // Check if items array reference changed (meaning items were updated)
    const itemsChanged = newItems !== current.items;
    const orderSummaryChanged = newOrderSummary !== current.orderSummary;
    const cartMetadataChanged = newCartMetadata !== current.cartMetadata;
    const appliedPromoCodeChanged =
      response.cartMetadata?.currentlyAppliedPromocode !== undefined &&
      response.cartMetadata.currentlyAppliedPromocode !==
        current.appliedPromoCode;

    // Only update state if something actually changed
    if (
      itemsChanged ||
      orderSummaryChanged ||
      cartMetadataChanged ||
      appliedPromoCodeChanged
    ) {
      const newState = {
        ...current,
        items: newItems,
        orderSummary: newOrderSummary,
        cartMetadata: newCartMetadata,
        appliedPromoCode:
          response.cartMetadata?.currentlyAppliedPromocode !== undefined
            ? response.cartMetadata.currentlyAppliedPromocode
            : current.appliedPromoCode,
        lastUpdated: new Date(),
        loading: false,
        error: null,
      };

      cartVar(newState);
    } else {
      // Even if nothing changed, ensure loading is false
      if (current.loading) {
        cartVar({
          ...current,
          loading: false,
        });
      }
    }
  },

  // Method to update available promo codes
  setAvailablePromoCodes: (promoCodes: any[] | null) => {
    const current = cartVar();
    // Only update if the promo codes have actually changed
    if (
      JSON.stringify(current.availablePromoCodes) !== JSON.stringify(promoCodes)
    ) {
      cartVar({
        ...current,
        availablePromoCodes: promoCodes,
      });
    }
  },

  // Optimistic updates for better UX
  optimisticAddItem: (item: ICartItem) => {
    const current = cartVar();
    const existingItemIndex = current.items.findIndex(
      (existingItem) => existingItem._id === item._id
    );

    let updatedItems: ICartItem[];
    if (existingItemIndex >= 0) {
      updatedItems = current.items.map((existingItem, index) =>
        index === existingItemIndex
          ? { ...existingItem, quantity: existingItem.quantity + item.quantity }
          : existingItem
      );
    } else {
      updatedItems = [...current.items, item];
    }

    cartVar({
      ...current,
      items: updatedItems,
      lastUpdated: new Date(),
    });
  },

  optimisticRemoveItem: (itemId: string) => {
    const current = cartVar();
    const updatedItems = current.items.filter((item) => item._id !== itemId);

    cartVar({
      ...current,
      items: updatedItems,
      lastUpdated: new Date(),
    });
  },

  optimisticUpdateQuantity: (itemId: string, quantity: number) => {
    const current = cartVar();
    const updatedItems = current.items
      .map((item) => (item._id === itemId ? { ...item, quantity } : item))
      .filter((item) => item.quantity > 0);

    cartVar({
      ...current,
      items: updatedItems,
      lastUpdated: new Date(),
    });
  },

  clearCart: () => {
    cartVar({
      ...initialCartState,
      lastUpdated: new Date(),
    });
  },

  // Reset to initial state (useful for logout)
  resetCart: () => {
    cartVar(initialCartState);
  },
};
